import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useClerk } from "@clerk/clerk-react";
import AuthLayout from "../components/AuthLayout";
import { useToast } from "../../../components/ui";
import {
  isUnknownGoogleAccount,
  isUnknownGoogleAccountError,
  takeGoogleOAuthIntent,
} from "../services/authService";
import { ROUTES } from "../../../routes/paths";

/** Longer than the 3s default: these land the user on a screen they did not
 *  ask for, so the message has to outlast the arrival. */
const FAILURE_TOAST_MS = 5000;

/**
 * Landing spot for the Google redirect. Nothing is rendered by the handshake
 * itself - it only reads the parameters Clerk appended to the URL, activates
 * the session and forwards - so this page keeps our own shell and spinner
 * around it and the user never sees a Clerk screen.
 *
 * `handleRedirectCallback` is called directly rather than through Clerk's
 * `<AuthenticateWithRedirectCallback>`, which is the same call in a one-line
 * effect with no error handling: a handshake that rejects outright leaves that
 * component sitting on the spinner for good. The `customNavigate` below also
 * keeps the destination inside the router when Clerk is willing to let it -
 * it often is not, and finishes with a top-level redirect of its own.
 */
const SsoCallbackPage: React.FC = () => {
  const clerk = useClerk();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // The handshake consumes the URL parameters, so it must run exactly once -
  // React 18 StrictMode invokes effects twice in development.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // Which screen sent the user to Google. Reading it clears the copy kept
    // for the trip, so this has to stay inside the guarded block.
    const intent = takeGoogleOAuthIntent(location.search);
    const isSignup = intent === "signup";

    /**
     * The one screen a handshake that signed nobody in is sent to, whoever
     * decides to send it - Clerk through the URLs below, or this screen after.
     * A log-in that fails has no account behind it, so it ends on sign up; a
     * sign-up that fails has one already, so it ends on log in.
     *
     * Both agreeing is the point. With Clerk still pointed at the log-in
     * screen, its navigation and this screen's raced each other and the user
     * watched the page change twice.
     */
    const deadEndUrl = isSignup ? ROUTES.login : ROUTES.signup;

    /**
     * Where a handshake that *worked* lands, sign-up or log-in alike.
     *
     * The trial screen is the one that asks — `can_start_trial` off
     * `GET /billing/subscription/` — and it forwards anyone who cannot take one
     * to the dashboard. Sending a log-in to the dashboard instead would put
     * `/dashboard` in the address bar of somebody who is about to be shown the
     * offer, which is a URL that was never true.
     *
     * `startGoogleSignup` was handed this same path on the way out, so Clerk's
     * own completion URL and the ones below cannot disagree.
     */
    const successUrl = ROUTES.trial;

    /**
     * The whole point of the intent: an address Clerk has never seen becomes
     * an account only on the sign-up route, which is the route that collected
     * DPDP consent first. On the log-in route the transfer is blocked, and
     * this is what tells the user why, and puts them where they can act on it.
     *
     * Which message is decided by the attempt Clerk leaves behind, but whether
     * to say anything at all is decided by the session: a log-in handshake
     * that ends without one did not sign anybody in, whatever the resource
     * says about it.
     *
     * Both end on `deadEndUrl`, the same screen Clerk was pointed at above -
     * for a log-in that is the sign-up screen, because log in is the one place
     * that cannot help a user with no account to log in to. Usually Clerk has
     * already taken the user there by the time this runs, and navigating a
     * second time to the screen already on display is what made it flicker, so
     * `arrivedAt` below keeps the message and skips the move.
     */
    let arrivedAt: string | null = null;
    let reported = false;
    const reportFailedLogin = () => {
      if (reported) return;
      reported = true;

      if (isUnknownGoogleAccount(clerk)) {
        toast.error(
          "No account found",
          "That Google account is not registered yet. Please sign up to continue.",
          FAILURE_TOAST_MS
        );
      } else {
        toast.error(
          "Sign-in could not be completed",
          "We could not sign you in with that Google account. Please sign up to continue.",
          FAILURE_TOAST_MS
        );
      }
      goTo(deadEndUrl);
    };

    /** Navigates unless Clerk already landed us there. */
    const goTo = (path: string) => {
      if (arrivedAt === path) return;
      arrivedAt = path;
      navigate(path, { replace: true });
    };

    /**
     * A Google sign-up that Clerk could not finish on its own.
     *
     * Clerk hands these to one of the URLs above, and the ones it picks for a
     * sign-up all point at the sign-up form - a screen that starts a brand new
     * email sign-up and knows nothing about the account half-created here. The
     * user is returned to where they began with no idea why, and going round
     * again only lands them here a second time.
     *
     * So the one step that is still ours to finish is finished: an address
     * Clerk wants verified is a code away, and the OTP screen sends it and
     * takes it. Anything else is a field only the Clerk instance's own
     * configuration can explain, so it is named rather than swallowed.
     */
    const reportIncompleteSignup = () => {
      const attempt = clerk.client?.signUp;
      if (!attempt || attempt.status !== "missing_requirements") return;

      if (attempt.unverifiedFields?.includes("email_address")) {
        goTo(ROUTES.verifyOtp);
        return;
      }

      const missing = attempt.missingFields.join(", ");
      toast.error(
        "Sign-up could not be completed",
        missing
          ? `Google did not provide everything this account needs (${missing}). Please sign up with your email instead.`
          : "We could not finish creating your account with that Google account. Please sign up with your email instead.",
        FAILURE_TOAST_MS
      );
      goTo(ROUTES.signup);
    };

    clerk
      .handleRedirectCallback(
        {
          transferable: isSignup,
          signInFallbackRedirectUrl: successUrl,
          signUpFallbackRedirectUrl: successUrl,
          signInForceRedirectUrl: successUrl,
          signUpForceRedirectUrl: successUrl,

          // Every branch of the handshake that cannot finish on its own picks
          // one of the URLs below, and each one left unset defaults to the
          // Clerk-hosted Account Portal - a screen this app otherwise never
          // shows. A blocked transfer takes `signInUrl`, which is why it is
          // the one that matters most here.
          signInUrl: deadEndUrl,
          signUpUrl: ROUTES.signup,
          // No screens of our own for these: the instance is email-code only,
          // so they are unreachable in practice. Sending the user back to a
          // screen that works beats sending them out of the app.
          firstFactorUrl: deadEndUrl,
          secondFactorUrl: deadEndUrl,
          resetPasswordUrl: deadEndUrl,
          // Where a Google sign-up goes when Clerk still wants a field it
          // could not get from Google. The email code is the one of these the
          // app has a screen for - and that screen reads the attempt off
          // Clerk's client, so it does not need the router state it is
          // arriving here without.
          continueSignUpUrl: ROUTES.signup,
          verifyEmailAddressUrl: ROUTES.verifyOtp,
          verifyPhoneNumberUrl: ROUTES.signup,
        },
        async (to) => {
          const path = internalPath(to);
          if (path) {
            goTo(path);
          } else if (isSignup) {
            // Clerk wants to leave the app entirely - hand it to the browser,
            // which the router cannot do.
            window.location.href = to;
          } else {
            // A failed log-in has nowhere off-app worth going, and following
            // Clerk out would be a full page load out of a flow we can finish
            // ourselves. Kept in the router instead.
            goTo(deadEndUrl);
          }
        }
      )
      .then(() => {
        // Runs after the whole handshake, including the `setActive` that a
        // successful one ends with - so by here a session either exists or was
        // never going to.
        if (clerk.session) return;

        // Clerk legitimately sends a sign-up on to another step of its own
        // without a session yet, so this is not a failure the way it is for a
        // log-in - but it is not somewhere the user can be left either.
        if (isSignup) {
          reportIncompleteSignup();
          return;
        }

        reportFailedLogin();
      })
      .catch((error) => {
        // A handshake that cannot complete at all - a cancelled consent, a
        // link opened twice, expired parameters. Clerk's own fallbacks only
        // cover the cases it can resolve; without this the user would sit on
        // the spinner for good.
        if (!isSignup && isUnknownGoogleAccountError(error)) {
          reportFailedLogin();
          return;
        }

        toast.error(
          "Sign-in could not be completed",
          "Please try again.",
          FAILURE_TOAST_MS
        );
        console.error("SSO callback failed:", error);
        // The same screen as every other exit, so the page never changes twice.
        goTo(deadEndUrl);
      });
  }, [clerk, navigate, location.search, toast]);

  return (
    <AuthLayout>
      <div className="flex flex-col items-center gap-4 py-10">
        <span className="h-8 w-8 rounded-full border-2 border-[#DCE0E4] border-t-[#00C2CB] animate-spin" />
        <p className="text-[15px] text-[#62636C]">Finishing sign-in…</p>
      </div>

      {/* Clerk's bot check, which this screen needs as much as the consent
          screen does. A Google sign-up creates the account *here*: the
          handshake below turns the sign-in attempt into a sign-up by transfer,
          and that transfer is a `signUp.create` like any other. Without this
          element in the DOM at that moment Clerk cannot mount Smart CAPTCHA,
          falls back to the invisible widget, and the transfer fails - which it
          answers by sending the user to `signUpUrl`, ie. back to the sign-up
          form. Rendered rather than mounted in the effect because it has to be
          there before the effect runs. Empty, it takes no space. */}
      <div id="clerk-captcha" className="empty:hidden mt-4" />
    </AuthLayout>
  );
};

/** Clerk hands `customNavigate` either a path or a full URL. Returns the part
 *  the router can take, or null when the destination is off this origin. */
function internalPath(to: string): string | null {
  try {
    const url = new URL(to, window.location.origin);
    return url.origin === window.location.origin
      ? `${url.pathname}${url.search}${url.hash}`
      : null;
  } catch {
    return to.startsWith("/") ? to : null;
  }
}

export default SsoCallbackPage;
